import {
  Avatar,
  AvatarFallback,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@flexui-io/flexui-components';
import { useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useParams,
  useRouterState,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import {
  Check,
  ChevronDown,
  FolderKanban,
  Images,
  Languages,
  LayoutDashboard,
  Library,
  Laptop,
  LogOut,
  Menu,
  Monitor,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Presentation,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Sun,
} from 'lucide-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import type { ComponentType, ReactNode, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

import { type AppearancePreference, useAppearance } from '../app/appearance';
import { hasStoredSiteAppearance, readSiteAppearance, saveSiteAppearance, SITE_APPEARANCE_UPDATED_EVENT } from '../app/site-appearance/site-appearance-contract';
import { isSiteAppearanceStudioEnabled } from '../app/site-appearance/site-appearance-enabled';
import { ManageFullScreenAppearance } from '../app/site-appearance/manage-fullscreen-appearance';
import { useGetCompanyContext, useListCompanies } from '../generated/api/companies/companies';
import { useGetCurrentAccount, useLogout } from '../generated/api/auth/auth';
import { requireAuthenticatedSession } from '../features/auth/session-guard';
import { ErrorAppearance } from '../features/error-states/error-appearance';
import { ErrorState } from '../features/error-states/error-state';
import { useSessionFailure } from '../features/auth/use-session-failure';
import { getErrorRequestId, resolveErrorStateKind } from '../features/error-states/error-state-config';
import { NotificationsCenter } from '../features/collaboration/notifications-center';
import {
  isFullScreenRoute,
  isPresentationEditorRoute,
  isPresentationAudienceRoute,
  isPresentationPresenterRoute,
  isPresentationPreviewRoute,
  isTemplateStudioRoute,
} from '../features/manage/full-screen-route';
import { isManageNavigationActive } from '../features/manage/manage-navigation-active';
import { DesignLibraryMenu } from '../features/manage/design-library-menu';
import { resolveManageWorkspace } from '../features/manage/manage-workspace-context';
import { ShellContextSlotProvider } from '../features/manage/shell-context-slot';
import '../features/manage/manage-identity-appearance.css';

const SIDEBAR_STORAGE_KEY = 'slider:ui:sidebar:v1';
const MANAGE_APPEARANCE_STUDIO_ENABLED = isSiteAppearanceStudioEnabled();

// Keep the management studio optional and avoid bundling its 42-font editor on every route.
const ManageAppearanceStudio = lazy(() => import('../app/site-appearance/site-appearance-studio'));

export const Route = createFileRoute('/manage')({
  beforeLoad: ({ context, location }) =>
    requireAuthenticatedSession({ href: location.href, queryClient: context.queryClient }),
  component: ManageLayout,
  notFoundComponent: () => (
    <ErrorAppearance><ErrorState kind="404" /></ErrorAppearance>
  ),
  errorComponent: ManageRouteError,
});

type NavigationIcon = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

interface NavigationItem {
  icon: NavigationIcon;
  key:
    | 'overview'
    | 'projects'
    | 'presentations'
    | 'assets'
    | 'designLibrary'
    | 'aiActions';
  pathSegment:
    | 'dashboard'
    | 'projects'
    | 'presentations'
    | 'assets'
    | 'design-library'
    | 'ai-actions';
}

const navigation: NavigationItem[] = [
  { icon: LayoutDashboard, key: 'overview', pathSegment: 'dashboard' },
  { icon: FolderKanban, key: 'projects', pathSegment: 'projects' },
  { icon: Presentation, key: 'presentations', pathSegment: 'presentations' },
  { icon: Images, key: 'assets', pathSegment: 'assets' },
  { icon: Library, key: 'designLibrary', pathSegment: 'design-library' },
  { icon: Sparkles, key: 'aiActions', pathSegment: 'ai-actions' },
];

function SliderLogo({ collapsed = false }: { collapsed?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="manage-logo">
      <span aria-hidden="true">S</span>
      {!collapsed && <b>{t('brand.name')}</b>}
    </div>
  );
}

function NavLabel({ children, collapsed }: { children: ReactNode; collapsed: boolean }) {
  return collapsed ? <span className="sr-only">{children}</span> : <span>{children}</span>;
}

function ManageNavigation({
  collapsed,
  companySlug,
  isStylesTab,
  onNavigate,
  pathname,
}: {
  collapsed: boolean;
  companySlug: string | undefined;
  isStylesTab: boolean;
  onNavigate?: () => void;
  pathname: string;
}) {
  const { t } = useTranslation();

  return (
    <div className="manage-navigation-frame">
      <nav aria-label={t('manage.navigationLabel')} className="manage-primary-nav">
        {navigation.map(({ icon: Icon, key, pathSegment }) => {
          if (pathSegment === 'design-library') {
            return (
              <DesignLibraryMenu
                collapsed={collapsed}
                companySlug={companySlug}
                isStylesTab={isStylesTab}
                key={key}
                onNavigate={onNavigate}
                pathname={pathname}
              />
            );
          }
          const label = t(`manage.${key}`);
          const target = companySlug ? `/manage/${companySlug}/${pathSegment}` : null;
          const active = isManageNavigationActive(pathname, companySlug, pathSegment);
          const current: 'page' | 'location' | undefined =
            active ? (pathname === target ? 'page' : 'location') : undefined;

          if (companySlug && pathSegment === 'dashboard') {
            return (
              <Link
                aria-current={current}
                className={active ? 'active' : undefined}
                key={key}
                onClick={onNavigate}
                params={{ companySlug }}
                title={collapsed ? label : undefined}
                to="/manage/$companySlug/dashboard"
              >
                <Icon aria-hidden="true" size={18} />
                <NavLabel collapsed={collapsed}>{label}</NavLabel>
              </Link>
            );
          }

          if (companySlug && pathSegment === 'projects') {
            return (
              <Link
                aria-current={current}
                className={active ? 'active' : undefined}
                key={key}
                onClick={onNavigate}
                params={{ companySlug }}
                search={{ order: 'desc', page: 1, pageSize: 25, sort: 'updatedAt' }}
                title={collapsed ? label : undefined}
                to="/manage/$companySlug/projects"
              >
                <Icon aria-hidden="true" size={18} />
                <NavLabel collapsed={collapsed}>{label}</NavLabel>
              </Link>
            );
          }

          if (companySlug && pathSegment === 'presentations') {
            return (
              <Link
                aria-current={current}
                className={active ? 'active' : undefined}
                key={key}
                onClick={onNavigate}
                params={{ companySlug }}
                search={{ order: 'desc', page: 1, pageSize: 25, sort: 'updatedAt' }}
                title={collapsed ? label : undefined}
                to="/manage/$companySlug/presentations"
              >
                <Icon aria-hidden="true" size={18} />
                <NavLabel collapsed={collapsed}>{label}</NavLabel>
              </Link>
            );
          }

          if (companySlug && pathSegment === 'ai-actions') {
            return (
              <Link
                aria-current={current}
                className={active ? 'active' : undefined}
                key={key}
                onClick={onNavigate}
                params={{ companySlug }}
                search={{ page: 1, pageSize: 25, status: 'PENDING_REVIEW' }}
                title={collapsed ? label : undefined}
                to="/manage/$companySlug/ai-actions"
              >
                <Icon aria-hidden="true" size={18} />
                <NavLabel collapsed={collapsed}>{label}</NavLabel>
              </Link>
            );
          }

          if (companySlug && pathSegment === 'assets') {
            return (
              <Link
                aria-current={current}
                className={active ? 'active' : undefined}
                key={key}
                onClick={onNavigate}
                params={{ companySlug }}
                search={{ page: 1, pageSize: 25 }}
                title={collapsed ? label : undefined}
                to="/manage/$companySlug/assets"
              >
                <Icon aria-hidden="true" size={18} />
                <NavLabel collapsed={collapsed}>{label}</NavLabel>
              </Link>
            );
          }

          return (
            <span
              aria-current={active ? 'page' : undefined}
              aria-disabled="true"
              className={`manage-nav-unavailable${active ? ' active' : ''}`}
              key={key}
              title={collapsed ? label : undefined}
            >
              <Icon aria-hidden="true" size={18} />
              <NavLabel collapsed={collapsed}>{label}</NavLabel>
            </span>
          );
        })}
      </nav>
      {companySlug && (
        <nav aria-label={t('manage.secondaryNavigationLabel')} className="manage-secondary-nav">
          <Link
            activeOptions={{ exact: false }}
            activeProps={{ 'aria-current': 'page', className: 'active' }}
            onClick={onNavigate}
            params={{ companySlug }}
            search={{ page: 1, pageSize: 25 }}
            title={collapsed ? t('manage.settings') : undefined}
            to="/manage/$companySlug/settings/members"
          >
            <Settings aria-hidden="true" size={18} />
            <NavLabel collapsed={collapsed}>{t('manage.settings')}</NavLabel>
          </Link>
        </nav>
      )}
    </div>
  );
}

function AppearanceOptions({ appearance, onChange }: {
  appearance: AppearancePreference;
  onChange: (appearance: AppearancePreference) => void;
}) {
  const { t } = useTranslation();
  const items: Array<{ icon: NavigationIcon; label: string; value: AppearancePreference }> = [
    { icon: Monitor, label: t('manage.systemTheme'), value: 'system' },
    { icon: Sun, label: t('manage.lightTheme'), value: 'light' },
    { icon: Moon, label: t('manage.darkTheme'), value: 'dark' },
  ];
  return <div aria-label={t('manage.appearance')} className="manage-preference-options" role="group">
    {items.map(({ icon: Icon, label, value }) => <button
      aria-pressed={appearance === value} key={value} onClick={() => onChange(value)} type="button">
      <Icon aria-hidden="true" size={16} /><span>{label}</span>
      {appearance === value && <Check aria-hidden="true" className="menu-check" size={16} />}
    </button>)}
  </div>;
}

function ManageRouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <ErrorAppearance>
      <ErrorState
        kind={resolveErrorStateKind(error)}
        requestId={getErrorRequestId(error)}
        onRetry={() => { reset(); void router.invalidate(); }}
      />
    </ErrorAppearance>
  );
}

function ManageLayout() {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sessionExpired = useSessionFailure();
  const params = useParams({ strict: false });
  const companySlug = params.companySlug;
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const isStylesTab = useRouterState({
    select: (state) => new URLSearchParams(state.location.searchStr).get('tab') === 'styles',
  });
  const [appearance, setAppearance] = useAppearance();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'collapsed',
  );
  const [commandOpen, setCommandOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [savedStudioMode, setSavedStudioMode] = useState<AppearancePreference | null>(() =>
    MANAGE_APPEARANCE_STUDIO_ENABLED && hasStoredSiteAppearance() ? readSiteAppearance().mode : null,
  );
  const company = useGetCompanyContext(companySlug ?? '', {
    query: { enabled: Boolean(companySlug), staleTime: 30_000 },
  });
  const companies = useListCompanies({ query: { staleTime: 60_000 } });
  const account = useGetCurrentAccount({ query: { staleTime: 30_000 } });
  const companyData = company.data?.status === 200 ? company.data.data : undefined;
  const companyItems = companies.data?.status === 200 ? companies.data.data.items : [];
  const accountData = account.data?.status === 200 ? account.data.data : undefined;
  const workspace = resolveManageWorkspace(companySlug, companyData, companyItems);
  const workspaceSlug = workspace.slug;
  const workspaceName = workspace.name ?? t('manage.workspace');
  const activeAppearance = savedStudioMode ?? appearance;
  const accountName = accountData?.displayName ?? t('manage.account');
  const accountInitials = accountName.slice(0, 2).toLocaleUpperCase(i18n.language);
  const workspaceInitials = workspaceName.slice(0, 2).toLocaleUpperCase(i18n.language);
  const logout = useLogout({
    mutation: {
      onSuccess: () => {
        queryClient.clear();
        void navigate({ to: '/login' });
      },
    },
  });

  useEffect(() => {
    if (!MANAGE_APPEARANCE_STUDIO_ENABLED) return;
    const sync = () => setSavedStudioMode(hasStoredSiteAppearance() ? readSiteAppearance().mode : null);
    window.addEventListener(SITE_APPEARANCE_UPDATED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(SITE_APPEARANCE_UPDATED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const changeAppearance = (next: AppearancePreference) => {
    if (MANAGE_APPEARANCE_STUDIO_ENABLED && hasStoredSiteAppearance()) {
      // Keep the full committed Studio preset; only alter its color mode.
      return saveSiteAppearance({ ...readSiteAppearance(), mode: next });
    }
    setAppearance(next);
    return true;
  };

  useEffect(() => {
    const openCommand = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen((current) => !current);
      }
    };
    document.addEventListener('keydown', openCommand);
    return () => document.removeEventListener('keydown', openCommand);
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? 'collapsed' : 'expanded');
      return next;
    });
  };

  const goToDashboard = () => {
    setCommandOpen(false);
    if (workspaceSlug)
      void navigate({ to: '/manage/$companySlug/dashboard', params: { companySlug: workspaceSlug } });
  };
  const goToProjects = () => {
    setCommandOpen(false);
    if (workspaceSlug) {
      void navigate({
        to: '/manage/$companySlug/projects',
        params: { companySlug: workspaceSlug },
        search: { order: 'desc', page: 1, pageSize: 25, sort: 'updatedAt' },
      });
    }
  };
  const switchCompany = (slug: string) => {
    setCompanyOpen(false);
    void navigate({ to: '/manage/$companySlug/dashboard', params: { companySlug: slug } });
  };

  // A 401 after a cached auth guard means the user can no longer load private data.
  // Use one recovery screen, not a wall of "Try again" alerts on every page.
  if (sessionExpired) {
    return (
      <ErrorAppearance>
        <ErrorState kind="401" />
      </ErrorAppearance>
    );
  }

  // Declared after every hook so the rule of hooks holds: the shell still runs
  // its queries, it simply renders none of its own chrome.
  if (isFullScreenRoute(pathname)) {
    const authoringMode = isTemplateStudioRoute(pathname)
      ? 'template'
      : isPresentationEditorRoute(pathname)
        ? 'editor'
        : isPresentationPresenterRoute(pathname)
          ? 'presenter'
          : isPresentationAudienceRoute(pathname)
            ? 'audience'
            : isPresentationPreviewRoute(pathname)
              ? 'preview'
              : null;
    return authoringMode ? (
      <ManageFullScreenAppearance mode={authoringMode}>
        <Outlet />
      </ManageFullScreenAppearance>
    ) : (
      <Outlet />
    );
  }

  return (
    <ShellContextSlotProvider>
      {(slotRef) => (
        <div data-manage-appearance-scope className={`manage-shell${collapsed ? ' sidebar-collapsed' : ''}`}>
          <aside className="manage-sidebar">
            <div className="manage-brand-row">
              <SliderLogo collapsed={collapsed} />
              <button
                aria-label={t(collapsed ? 'manage.expandSidebar' : 'manage.collapseSidebar')}
                className="sidebar-toggle"
                onClick={toggleCollapsed}
                type="button"
              >
                {collapsed ? (
                  i18n.dir() === 'rtl'
                    ? <PanelRightOpen aria-hidden="true" size={18} />
                    : <PanelLeftOpen aria-hidden="true" size={18} />
                ) : i18n.dir() === 'rtl' ? (
                  <PanelRightClose aria-hidden="true" size={18} />
                ) : (
                  <PanelLeftClose aria-hidden="true" size={18} />
                )}
              </button>
            </div>
            <Popover onOpenChange={setCompanyOpen} open={companyOpen}>
              <PopoverTrigger asChild>
                <button
                  aria-label={t('manage.switchCompany')}
                  className="workspace-switcher"
                  type="button"
                >
                  <span aria-hidden="true" className="manage-company-avatar">{workspaceInitials}</span>
                  {!collapsed && <b dir="auto">{workspaceName}</b>}
                  {!collapsed && <ChevronDown aria-hidden="true" size={15} />}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="company-switcher-popover" sideOffset={8}>
                <p className="popover-heading">{t('manage.companies')}</p>
                {companyItems.map((item) => (
                  <button
                    aria-current={item.slug === workspaceSlug ? 'true' : undefined}
                    className="company-option"
                    key={item.id}
                    onClick={() => switchCompany(item.slug)}
                    type="button"
                  >
                    <span aria-hidden="true" className="manage-company-avatar">{item.name.slice(0, 2).toLocaleUpperCase(i18n.language)}</span>
                    <b dir="auto">{item.name}</b>
                    {item.slug === workspaceSlug && <Check aria-hidden="true" className="company-option-check" size={16} />}
                  </button>
                ))}
                {workspaceSlug && (
                  <Link
                    className="popover-link"
                    onClick={() => setCompanyOpen(false)}
                    params={{ companySlug: workspaceSlug }}
                    search={{ page: 1, pageSize: 25 }}
                    to="/manage/$companySlug/settings/members"
                  >
                    <Settings aria-hidden="true" size={16} />
                    {t('manage.companySettings')}
                  </Link>
                )}
              </PopoverContent>
            </Popover>
            <ManageNavigation
              collapsed={collapsed}
              companySlug={workspaceSlug}
              isStylesTab={isStylesTab}
              pathname={pathname}
            />
          </aside>
          <div className="manage-workspace">
            <header className="manage-topbar">
              <div className="manage-topbar-start">
                <Sheet onOpenChange={setMobileOpen} open={mobileOpen}>
                  <SheetTrigger asChild>
                    <button aria-label={t('common.openMenu')} className="mobile-menu" type="button">
                      <Menu aria-hidden="true" size={21} />
                    </button>
                  </SheetTrigger>
                  <SheetContent
                    className="manage-mobile-sheet"
                    closeLabel={t('common.close')}
                    side={i18n.dir() === 'rtl' ? 'right' : 'left'}
                  >
                    <SheetHeader>
                      <SheetTitle>
                        <SliderLogo />
                      </SheetTitle>
                    </SheetHeader>
                    <div className="mobile-workspace-name">
                      <span aria-hidden="true" className="manage-company-avatar">{workspaceInitials}</span>
                      <b dir="auto">{workspaceName}</b>
                    </div>
                    {companyItems.length > 1 && (
                      <div aria-label={t('manage.companies')} className="mobile-company-list">
                        {companyItems.map((item) => (
                          <button
                            aria-current={item.slug === workspaceSlug ? 'page' : undefined}
                            key={item.id}
                            onClick={() => {
                              setMobileOpen(false);
                              switchCompany(item.slug);
                            }}
                            type="button"
                          >
                            <bdi>{item.name}</bdi>
                          </button>
                        ))}
                      </div>
                    )}
                    <ManageNavigation
                      collapsed={false}
                      companySlug={workspaceSlug}
                      isStylesTab={isStylesTab}
                      onNavigate={() => setMobileOpen(false)}
                      pathname={pathname}
                    />
                  </SheetContent>
                </Sheet>
                {/* A nested route may render its own identity here; the workspace
                name is the fallback when nothing claims the slot. */}
                <span className="manage-breadcrumb"><bdi>{workspaceName}</bdi></span>
                <div className="manage-topbar-context" ref={slotRef} />
              </div>
              <div className="manage-topbar-actions">
                {MANAGE_APPEARANCE_STUDIO_ENABLED && (
                  <Suspense fallback={null}><ManageAppearanceStudio scope="manage" /></Suspense>
                )}
                <Popover onOpenChange={setAppearanceOpen} open={appearanceOpen}>
                  <PopoverTrigger asChild>
                    <button aria-label={t('manage.appearance')} className="topbar-icon-button manage-preference-trigger"
                      title={t('manage.appearance')} type="button">
                      {activeAppearance === 'dark' ? <Moon aria-hidden="true" size={18} /> :
                        activeAppearance === 'light' ? <Sun aria-hidden="true" size={18} /> :
                          <Monitor aria-hidden="true" size={18} />}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="manage-preference-popover" sideOffset={8}>
                    <p className="popover-heading">{t('manage.appearance')}</p>
                    <AppearanceOptions appearance={activeAppearance} onChange={(next) => {
                      if (changeAppearance(next)) setAppearanceOpen(false);
                    }} />
                  </PopoverContent>
                </Popover>
                <Popover onOpenChange={setLanguageOpen} open={languageOpen}>
                  <PopoverTrigger asChild>
                    <button aria-label={t('manage.language')} className="topbar-icon-button manage-preference-trigger"
                      title={t('manage.language')} type="button">
                      <Languages aria-hidden="true" size={18} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="manage-preference-popover" sideOffset={8}>
                    <p className="popover-heading">{t('manage.language')}</p>
                    <div aria-label={t('manage.language')} className="manage-preference-options" role="group">
                      {(['en', 'fa'] as const).map((locale) => <button
                        aria-pressed={i18n.resolvedLanguage === locale} key={locale}
                        onClick={() => { void i18n.changeLanguage(locale); setLanguageOpen(false); }} type="button">
                        <Languages aria-hidden="true" size={16} />
                        <span>{locale === 'en' ? 'English' : 'فارسی'}</span>
                        {i18n.resolvedLanguage === locale && <Check aria-hidden="true" className="menu-check" size={16} />}
                      </button>)}
                    </div>
                  </PopoverContent>
                </Popover>
                <button
                  className="command-trigger"
                  onClick={() => setCommandOpen(true)}
                  type="button"
                >
                  <Search aria-hidden="true" size={17} />
                  <span>{t('manage.search')}</span>
                  <kbd>{navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'} K</kbd>
                </button>
                {workspaceSlug && <NotificationsCenter companySlug={workspaceSlug} />}
                <Popover onOpenChange={setProfileOpen} open={profileOpen}>
                  <PopoverTrigger asChild>
                    <button
                      aria-label={t('manage.profile')}
                      className="profile-button"
                      type="button"
                    >
                      <Avatar size="sm">
                        <AvatarFallback className="manage-profile-avatar">{accountInitials}</AvatarFallback>
                      </Avatar>
                      <ChevronDown aria-hidden="true" size={15} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="profile-popover" sideOffset={8}>
                    <div className="profile-identity">
                      <Avatar>
                        <AvatarFallback className="manage-profile-avatar">{accountInitials}</AvatarFallback>
                      </Avatar>
                      <div>
                        <b dir="auto">{accountName}</b>
                        <span dir="auto">{accountData?.email ?? companyData?.role ?? workspaceName}</span>
                      </div>
                    </div>
                    <div className="profile-menu-links">
                      <Link onClick={() => setProfileOpen(false)} to="/manage/account/security">
                        <ShieldCheck aria-hidden="true" size={16} />
                        {t('manage.security')}
                      </Link>
                      <Link onClick={() => setProfileOpen(false)} to="/manage/account/sessions">
                        <Laptop aria-hidden="true" size={16} />
                        {t('manage.sessions')}
                      </Link>
                    </div>
                    <div className="profile-signout-section">
                      <div aria-hidden="true" className="profile-signout-divider" role="separator" />
                      <button
                        className="profile-signout"
                        disabled={logout.isPending}
                        onClick={() => logout.mutate()}
                        type="button"
                      >
                        <LogOut aria-hidden="true" size={16} />
                        {t(logout.isPending ? 'manage.signingOut' : 'manage.signOut')}
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </header>
            <main className="manage-content">
              <Outlet />
            </main>
          </div>
          <CommandDialog
            description={t('manage.commandDescription')}
            onOpenChange={setCommandOpen}
            open={commandOpen}
            title={t('manage.commandTitle')}
          >
            <CommandInput placeholder={t('manage.commandPlaceholder')} />
            <CommandList>
              <CommandEmpty>{t('manage.commandEmpty')}</CommandEmpty>
              <CommandGroup heading={t('manage.navigationLabel')}>
                <CommandItem onSelect={goToDashboard}>
                  <LayoutDashboard aria-hidden="true" />
                  {t('manage.overview')}
                </CommandItem>
                <CommandItem onSelect={goToProjects}>
                  <FolderKanban aria-hidden="true" />
                  {t('manage.projects')}
                </CommandItem>
                <CommandItem
                  onSelect={() => {
                    setCommandOpen(false);
                    void navigate({ to: '/manage/account/security' });
                  }}
                >
                  <ShieldCheck aria-hidden="true" />
                  {t('manage.security')}
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </CommandDialog>
        </div>
      )}
    </ShellContextSlotProvider>
  );
}
